/**
 * Ticket Search Component
 * Search input with debounce
 */

import React, { useState, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { debounce } from '@/utils/helpers';

interface TicketSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export function TicketSearch({
  value,
  onChange,
  placeholder = 'Search tickets...',
}: TicketSearchProps) {
  const [localValue, setLocalValue] = useState(value);

  // Debounced onChange
  useEffect(() => {
    const debouncedChange = debounce((val: string) => {
      onChange(val);
    }, 300);

    debouncedChange(localValue);
  }, [localValue, onChange]);

  // Sync with external value changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleClear = () => {
    setLocalValue('');
    onChange('');
  };

  return (
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <Search className="h-5 w-5 text-gray-400" />
      </div>
      <input
        type="text"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        placeholder={placeholder}
        className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
      />
      {localValue && (
        <button
          onClick={handleClear}
          className="absolute inset-y-0 right-0 pr-3 flex items-center"
        >
          <X className="h-5 w-5 text-gray-400 hover:text-gray-600" />
        </button>
      )}
    </div>
  );
}
